import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fdatetime } from "@/lib/format";
import { Loader, PageTitle } from "@/components/common";
import { DataView } from "@/components/console/DataView";
import { DateField } from "@/components/DateField";
import { SUP_STATUS } from "@/pages/member/Commerce";
import { ImageManager, ImagesEditor } from "@/components/member/OpImages";

const EMPTY = { type: "reunion_mensuelle", title: "", starts_at: "", place: "", programme: "", capacity: 50, price_member: 10000, price_guest: 25000, status: "publie" };

function Scanner({ eventId, onScanned }) {
  const [code, setCode] = useState("");
  const [last, setLast] = useState(null);
  const video = useRef(null);
  const [cam, setCam] = useState(false);
  const scan = async (c) => {
    try {
      const { data } = await api.post("/commerce/admin/events/scan", { code: c, event_id: eventId });
      setLast({ ok: true, text: `Entrée validée : ${data.holder}${data.guest ? " (invité)" : ""}` });
      onScanned();
    } catch (e) {
      setLast({ ok: false, text: errMsg(e) });
    }
    setCode("");
  };
  useEffect(() => {
    if (!cam) return undefined;
    let stream;
    let timer;
    let busy = false;
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
        video.current.srcObject = stream;
        await video.current.play();
        const det = new window.BarcodeDetector({ formats: ["qr_code"] });
        timer = setInterval(async () => {
          if (busy) return;
          const found = await det.detect(video.current).catch(() => []);
          if (found[0]) {
            busy = true;
            await scan(found[0].rawValue);
            setTimeout(() => { busy = false; }, 2500);
          }
        }, 500);
      } catch {
        toast.error("Caméra ou lecture QR indisponible sur ce navigateur : saisissez le code.");
        setCam(false);
      }
    })();
    return () => {
      clearInterval(timer);
      stream?.getTracks().forEach((t) => t.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cam]);
  return (
    <div className="space-y-2" data-testid="ticket-scanner">
      <div className="flex gap-2">
        <input data-testid="scan-code-input" className="mr-input font-mono" placeholder="Code du billet" value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => e.key === "Enter" && code && scan(code)} />
        <button data-testid="scan-submit" className="mr-btn-primary px-3" disabled={!code} onClick={() => scan(code)}>Valider</button>
        {"BarcodeDetector" in window && <button data-testid="scan-camera" className="mr-btn-secondary px-3" onClick={() => setCam(!cam)}>{cam ? "Arrêter" : "Caméra"}</button>}
      </div>
      {cam && <video ref={video} className="w-full max-w-sm" muted playsInline />}
      {last && <p data-testid="scan-result" className={`text-sm font-medium ${last.ok ? "text-mr-accent" : "text-mr-danger"}`}>{last.text}</p>}
    </div>
  );
}

function EventTickets({ e }) {
  const [ts, setTs] = useState(null);
  const load = () => api.get(`/commerce/admin/events/${e.id}/tickets`).then(({ data }) => setTs(data));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [e.id]);
  return (
    <div className="space-y-3">
      <Scanner eventId={e.id} onScanned={load} />
      {ts && <ul className="text-sm divide-y divide-mr-border">{ts.map((t) => <li key={t.id} className="py-1.5" data-testid="console-ticket-row">{t.name} · {{ paye: "réglé", reserve: "paiement à valider", liste_attente: "liste d'attente", annule: "annulé" }[t.status] || t.status}{t.attended_at ? ` · entré à ${fdatetime(t.attended_at)}` : ""}</li>)}</ul>}
    </div>
  );
}

const EV_TYPES = [["reunion_mensuelle", "Rencontre mensuelle"], ["diner_affaires", "Dîner d'affaires"], ["atelier", "Atelier"], ["formation", "Formation"], ["visite_entreprise", "Visite d'entreprise"], ["presentation_projet", "Présentation de projet"]];
const EV_STATUS = [["publie", "Publié"], ["brouillon", "Brouillon"], ["termine", "Terminé"], ["annule", "Annulé"]];
const label = (list, k) => (list.find(([v]) => v === k) || [k, k])[1];

export function ConsoleEvents() {
  const [items, setItems] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [images, setImages] = useState([]);
  const load = () => api.get("/commerce/events").then(({ data }) => setItems(data));
  useEffect(() => {
    load();
  }, []);
  const create = () => api.post("/commerce/admin/events", { ...f, starts_at: new Date(f.starts_at).toISOString(), capacity: Number(f.capacity), price_member: Number(f.price_member), price_guest: Number(f.price_guest), photo_file_ids: images })
    .then(() => { toast.success("Événement publié"); setF(EMPTY); setImages([]); load(); }).catch((e) => toast.error(errMsg(e)));
  if (!items) return <Loader />;
  return (
    <div className="mr-fade-up space-y-6">
      <PageTitle eyebrow="Événements" title="Événements et billetterie" />
      <DataView page="events" title="Événements" rows={items} empty="Aucun événement"
        columns={[{ key: "title", label: "Titre", className: "font-medium" }, { key: "type", label: "Type", value: (e) => label(EV_TYPES, e.type) }, { key: "starts_at", label: "Date", value: (e) => fdatetime(e.starts_at), className: "tabular whitespace-nowrap" },
          { key: "place", label: "Lieu" }, { key: "seats", label: "Places", value: (e) => `${e.registered}/${e.capacity}`, className: "tabular" }, { key: "status", label: "Statut", value: (e) => label(EV_STATUS, e.status) }]}
        filters={[{ key: "status", label: "Statut", options: EV_STATUS }, { key: "type", label: "Type", options: EV_TYPES }]}
        kpis={[{ key: "upcoming", label: "À venir", value: (r) => r.filter((e) => new Date(e.starts_at) > new Date()).length }, { key: "seats", label: "Places réservées", value: (r) => r.reduce((s, e) => s + (e.registered || 0), 0) },
          { key: "fill", label: "Remplissage moyen", value: (r) => (r.length ? `${Math.round((r.reduce((s, e) => s + (e.registered || 0) / (e.capacity || 1), 0) / r.length) * 100)} %` : "—") }, { key: "total", label: "Événements", value: (r) => r.length }]}
        detailExtra={(e) => <div className="space-y-3"><ImagesEditor ids={e.photo_file_ids} endpoint={`/commerce/events/${e.id}/images`} onSaved={load} testId="console-event-images" /><EventTickets e={e} /></div>} />
      <div className="mr-card space-y-2" data-testid="event-create-form"><p className="font-medium">Nouvel événement</p>
        <select data-testid="event-type-select" className="mr-input" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}>
          {EV_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <div className="block text-sm">Date et heure<DateField withTime testId="event-starts_at-input" className="mt-1" value={f.starts_at} onChange={(v) => setF({ ...f, starts_at: v })} fromYear={new Date().getFullYear()} /></div>
        {[["title", "Titre", "text"], ["place", "Lieu", "text"], ["programme", "Programme", "text"], ["capacity", "Capacité", "number"], ["price_member", "Prix membre (FCFA)", "number"], ["price_guest", "Prix invité (FCFA)", "number"]].map(([k, l, t]) => (
          <label key={k} className="block text-sm">{l}<input data-testid={`event-${k}-input`} type={t} className="mr-input mt-1" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} /></label>))}
        <p className="text-sm">Images</p><ImageManager value={images} onChange={setImages} />
        <button data-testid="event-create-button" className="mr-btn-primary" disabled={f.title.length < 3 || !f.starts_at} onClick={create}>Publier</button>
      </div>
    </div>
  );
}

export function ConsoleSuppliers() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/commerce/suppliers").then(({ data }) => setItems(data));
  useEffect(() => {
    load();
  }, []);
  const save = (s, status) => {
    const checks = status === "verifie" ? (window.prompt("Vérifications documentées (séparées par ;)", (s.checks || []).join("; ")) || "").split(";").map((x) => x.trim()).filter(Boolean) : s.checks || [];
    api.patch(`/commerce/suppliers/${s.id}`, { status, checks, contacts_restricted: status === "verifie" && s.contacts_restricted, hidden: false }).then(() => { toast.success("Statut mis à jour"); load(); }).catch((e) => toast.error(errMsg(e)));
  };
  if (!items) return <Loader />;
  return (
    <div className="mr-fade-up">
      <PageTitle eyebrow="Annuaire" title="Fournisseurs" />
      <DataView page="suppliers" title="Fournisseurs" rows={items} empty="Aucun fournisseur"
        columns={[{ key: "name", label: "Nom", className: "font-medium" }, { key: "categories", label: "Catégories", value: (s) => (s.categories || []).join(", ") }, { key: "city", label: "Ville" }, { key: "status", label: "Statut", value: (s) => SUP_STATUS[s.status] },
          { key: "scam_reports", label: "Signalements", className: "tabular" }, { key: "checks", label: "Vérifications", value: (s) => (s.checks || []).join("; "), hidden: true }]}
        filters={[{ key: "status", label: "Statut", options: Object.entries(SUP_STATUS) }, { key: "city", label: "Ville", options: [...new Set(items.map((s) => s.city).filter(Boolean))].map((c) => [c, c]) }]}
        kpis={[{ key: "total", label: "Fournisseurs", value: (r) => r.length }, { key: "verified", label: "Vérifiés", value: (r) => r.filter((s) => s.status === "verifie").length },
          { key: "reports", label: "Avec signalement", value: (r) => r.filter((s) => s.scam_reports > 0).length }]}
        detailExtra={(s) => <label className="block text-sm">Changer le statut<select data-testid="console-supplier-status" className="mr-input mt-1" value={s.status} onChange={(e) => save(s, e.target.value)}>{Object.entries(SUP_STATUS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>} />
    </div>
  );
}
